import { type ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxMultipleLabel,
  type ComboboxPresetBaseProps,
  type ComboboxRootProps,
  ComboboxValue,
  useComboboxAnchor,
} from './combobox';
import {
  buildVirtualizedHighlightHandler,
  type CreatableMarker,
  type CreatableProps,
  isCreatable,
  type QueryProps,
  useCreatableHelpers,
  VirtualizedList,
  type VirtualizerHandle,
} from './virtualized-internals';

export type PaginatedMultiComboboxProps<T> = ComboboxPresetBaseProps<T> &
  Pick<
    ComboboxRootProps<T, true>,
    'value' | 'onValueChange' | 'isItemEqualToValue' | 'itemToStringLabel' | 'disabled'
  > &
  Pick<ComponentProps<typeof ComboboxChipsInput>, 'placeholder'> &
  Omit<ComponentProps<typeof ComboboxChips>, 'value' | 'onValueChange' | 'children'> &
  QueryProps &
  CreatableProps<T> & {
    /** Whether a page is currently in-flight. Suppresses repeat `onLoadMore` fires while true. */
    isLoading?: boolean;
    /** Whether more pages remain. When false, the tail probe is not rendered. */
    hasMore?: boolean;
    /** Fired when the user scrolls near the bottom of the loaded set. Detected via an IntersectionObserver on a 1px probe element after the virtualized rows. */
    onLoadMore?: () => void;
  };

type InFlightState<T> = { status: 'idle' } | { status: 'pending' | 'sticky'; value: T[] };

const IDLE: InFlightState<never> = { status: 'idle' };

export const PaginatedMultiCombobox = <T,>({
  items,
  value,
  onValueChange,
  getKey,
  getLabel,
  isItemEqualToValue,
  itemToStringLabel,
  placeholder,
  emptyMessage,
  disabled,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  query,
  onQueryChange,
  onCreate,
  renderCreateOption,
  estimateSize,
  id,
  collapseMultiple = false,
  ...chipsProps
}: PaginatedMultiComboboxProps<T>) => {
  const { t } = useTranslation();
  const anchor = useComboboxAnchor();
  const virtualizerRef = useRef<VirtualizerHandle | null>(null);
  const onItemHighlighted = useMemo(() => buildVirtualizedHighlightHandler(virtualizerRef), []);

  const isServerFilter = query !== undefined && onQueryChange !== undefined;
  const [internalQuery, setInternalQuery] = useState('');
  const effectiveQuery = isServerFilter ? query : internalQuery;
  const [inFlight, setInFlight] = useState<InFlightState<T>>(IDLE);

  const { itemsForView, localFilter, stableItemToStringLabel } = useCreatableHelpers(
    items,
    getLabel,
    itemToStringLabel,
    onCreate,
    effectiveQuery
  );

  // Default to key-based equality so cloned values (e.g. round-tripped through react-hook-form) still match items.
  const defaultIsItemEqualToValue = useCallback(
    (item: T, v: T) => {
      const itemIsCreatable = isCreatable(item);
      const valueIsCreatable = isCreatable(v);
      if (itemIsCreatable || valueIsCreatable) {
        return itemIsCreatable && valueIsCreatable && item.__create === v.__create;
      }
      return getKey(item) === getKey(v);
    },
    [getKey]
  );
  const effectiveIsItemEqualToValue = isItemEqualToValue ?? defaultIsItemEqualToValue;

  const handleValueChange = useCallback<NonNullable<ComboboxRootProps<T, true>['onValueChange']>>(
    async (next, details) => {
      const hasCreatable = next.some(isCreatable);

      if (hasCreatable && inFlight.status === 'sticky') {
        setInFlight({ status: 'sticky', value: next });
        onValueChange?.(next.filter((item) => !isCreatable(item)) as T[], details);
        return;
      }

      if (hasCreatable && onCreate) {
        const creatable = next.find(isCreatable) as unknown as CreatableMarker;
        setInFlight({ status: 'pending', value: next });

        try {
          const created = await onCreate(creatable.__create);
          const cleaned = next.filter((item) => !isCreatable(item)) as T[];
          onValueChange?.([...cleaned, created], details);
          setInFlight(IDLE);
          if (isServerFilter) {
            onQueryChange('');
          } else {
            setInternalQuery('');
          }
        } catch {
          setInFlight({ status: 'sticky', value: next });
        }

        return;
      }

      setInFlight(IDLE);
      onValueChange?.(next, details);
    },
    [onCreate, onValueChange, isServerFilter, onQueryChange, inFlight.status]
  );

  const handleInputValueChange = useCallback(
    (next: string) => {
      if (inFlight.status !== 'idle') {
        setInFlight(IDLE);
      }
      if (isServerFilter) {
        onQueryChange(next);
      } else {
        setInternalQuery(next);
      }
    },
    [inFlight.status, isServerFilter, onQueryChange]
  );

  const valueForRoot = inFlight.status === 'idle' ? (value ?? []) : inFlight.value;

  return (
    <Combobox
      multiple
      virtualized
      items={itemsForView}
      value={valueForRoot}
      onValueChange={handleValueChange}
      disabled={disabled}
      itemToStringLabel={stableItemToStringLabel}
      isItemEqualToValue={effectiveIsItemEqualToValue}
      inputValue={isServerFilter ? query : undefined}
      onInputValueChange={handleInputValueChange}
      filter={isServerFilter ? null : localFilter}
      onItemHighlighted={onItemHighlighted}
    >
      <ComboboxChips ref={anchor} collapseMultiple={collapseMultiple} disabled={disabled} {...chipsProps}>
        <ComboboxValue>
          {collapseMultiple && valueForRoot.length > 1 ? (
            <ComboboxMultipleLabel
              selectedItems={valueForRoot.filter((item) => !isCreatable(item))}
              itemToString={getLabel}
            />
          ) : (
            valueForRoot.map((item) => {
              if (isCreatable(item)) {
                return <ComboboxChip key={`__create__${item.__create}`}>{item.__create}</ComboboxChip>;
              }
              return <ComboboxChip key={getKey(item)}>{getLabel(item)}</ComboboxChip>;
            })
          )}
        </ComboboxValue>
        <ComboboxChipsInput
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={chipsProps['aria-invalid']}
        />
      </ComboboxChips>
      <ComboboxContent anchor={anchor}>
        <ComboboxEmpty>{isLoading ? t('combobox-loading') : (emptyMessage ?? t('combobox-empty'))}</ComboboxEmpty>
        <VirtualizedList
          items={itemsForView}
          getKey={getKey}
          getLabel={getLabel}
          estimateSize={estimateSize}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          renderCreateOption={renderCreateOption}
          isServerFilter={isServerFilter}
          virtualizerRef={virtualizerRef}
        />
      </ComboboxContent>
    </Combobox>
  );
};
